import { useCallback, useState } from 'react';
import { formatInstantDate } from '../../lib/format';
import type { AdminBusinessRead } from '../../types/api';
import { AdminBusinessPanel } from './AdminBusinessPanel';
import { fetchAdminBusinesses } from './adminApi';
import { usePagedList } from './usePagedList';

function businessId(business: AdminBusinessRead): string {
  return business.id;
}

/**
 * Every business in the deployment, with the two numbers a support request is
 * usually about: how many generations this month, and out of how many.
 *
 * `quota_override` is shown as its own marker rather than folded into the limit,
 * because "50 because that is the default" and "50 because someone set it" are
 * different facts and only one of them is safe to change without asking.
 */
export function AdminBusinessList() {
  const [selected, setSelected] = useState<string | null>(null);
  const list = usePagedList(fetchAdminBusinesses, businessId);

  const clear = useCallback(() => setSelected(null), []);

  return (
    <div className="admin-pane">
      {list.isLoading ? (
        <p className="kb-section__status" role="status">
          Loading businesses
        </p>
      ) : list.items.length === 0 && !list.error ? (
        <p className="admin-empty">No businesses on this deployment yet.</p>
      ) : (
        <div className="learning-scroll">
          <table className="admin-table">
            <thead>
              <tr>
                <th scope="col">Business</th>
                <th scope="col">This month</th>
                <th scope="col">Allowance</th>
                <th scope="col">Created</th>
              </tr>
            </thead>
            <tbody>
              {list.items.map((business) => (
                <tr
                  key={business.id}
                  className={business.id === selected ? 'admin-table__row--open' : undefined}
                >
                  <td>
                    <button
                      className="admin-table__open"
                      type="button"
                      onClick={() => setSelected(business.id)}
                    >
                      {business.name}
                    </button>
                  </td>
                  <td className="admin-table__number">{business.monthly_used}</td>
                  <td className="admin-table__number">
                    {business.quota_limit}
                    {business.quota_override !== null ? (
                      <span className="admin-mark" title="Support-set allowance">
                        override
                      </span>
                    ) : null}
                  </td>
                  <td className="admin-table__number">{formatInstantDate(business.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {list.error ? (
        <div className="admin-pane__foot">
          <p className="form__error" role="alert">
            {list.error}
          </p>
          <button className="button" type="button" onClick={list.retry}>
            Try again
          </button>
        </div>
      ) : list.hasMore ? (
        <div className="admin-pane__foot">
          <button
            className="button"
            type="button"
            onClick={list.loadMore}
            disabled={list.isLoadingMore}
          >
            {list.isLoadingMore ? 'Loading' : 'Load more'}
          </button>
        </div>
      ) : null}

      {selected ? (
        <AdminBusinessPanel key={selected} businessId={selected} onClose={clear} />
      ) : null}
    </div>
  );
}
